/*
  Phatstix — "DON'T CLICK ME" (Thom, 31 Aug 2026).

  THE CHOREOGRAPHY, which is the joke and matters more than the effect:
    1. SLIDE   the clicked advert detaches and slides off the side of the screen (700ms)
    2. BEAT    nothing happens for a moment. "ok, it just slid away, that was weird" (900ms)
    3. STORM   all hell breaks loose (3750ms)
    4. RETURN  the advert fades back into place, exactly as it started (600ms)
  Thom's words: "So its like an ok, it just slid away, that was weird, OMFG WHAT IS HAPPENING!"

  "loads of the boxes fly around the page... really manic, quite funny. all with flashing buttons,
   something really 90s manic internet." Then, on seeing v1: "whilst good, is like 10% of what it
   needs to be! it needs to be INSANITY". And: make it 5s.

  So this is not a CSS keyframe any more. It is a real requestAnimationFrame loop: every clone
  carries a velocity, a spin and a wobble, bounces off all four walls, and gets kicked again on
  every wave. Waves keep spawning for the whole run, so the screen fills rather than clearing.
  On top of the boxes: the page itself shakes and hue-strobes, and every button in the storm
  flashes. It is meant to be too much.

  THE REAL PAGE IS NEVER TOUCHED. Every flying box is a clone inside a fixed overlay which is
  deleted at the end, and the only thing applied to the document is one class that is removed in the
  same teardown. So there is no state to restore and nothing can be left half-broken if someone
  navigates away mid-storm.

  Clones are aria-hidden, tabindex -1, href-stripped and disabled: this is decoration, and a screen
  reader should not be told that forty adverts just appeared.
  prefers-reduced-motion gets one honest shake instead of the storm.
*/
(function () {
  'use strict';

  var DURATION = 3750;        // 5s, then trimmed 25% on Thom's call: "goes on maybe 25% too long"
  var WAVE_MS = 340;          // a fresh batch this often, so it keeps escalating. Tightened with the
                              // shorter run so the screen still fills before it stops.
  var PER_WAVE = 21;          // clones per wave IN TOTAL, not per source. It used to be per source,
                              // which made the whole effect depend on how many adverts an article
                              // happened to have: a short post gets ONE injected and the storm built
                              // at a third of the rate, so it read as "it just flashes colours"
                              // (Thom, 31 Aug, on the Anthropologie post). Density is now the same
                              // on every article.
  var MAX_LIVE = 90;          // hard ceiling, or a long article's four ads melt a phone

  var SLIDE_MS = 700;         // the advert leaves
  var BEAT_MS = 900;          // ...and nothing happens. This pause is the whole joke.
  var RETURN_MS = 600;        // it fades back afterwards

  var layer = null, raf = null, waveTimer = null, endTimer = null;
  var items = [], running = false;
  var timers = [];            // every phase handle, so a teardown can cancel mid-sequence
  var hidden = null;          // the real advert we took away, and must put back
  var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rand(a, b) { return a + Math.random() * (b - a); }

  /* The clones hang off <html>, OUTSIDE .ph-art, because a transformed ancestor would re-base them
     (see the appendChild note below). That means they stop inheriting the article's custom
     properties, so the advert re-rendered with a different shadow and metrics the instant it was
     cloned - it visibly twitched and changed size before it moved (Thom, 31 Aug).
     Copying the tokens onto the host element restores the exact same rendering. Read once per run
     from the live article, so nothing here has to know their values. */
  var ART_TOKENS = ['--art-ink','--art-dim','--art-line','--art-paper','--art-back','--art-accent',
                    '--art-serif','--art-display','--art-mono','--ph-w98-drop','--ph-w98-client'];
  function carryTokens(host, from) {
    var src = from || document.querySelector('.ph-art');
    if (!src) return;
    var cs = getComputedStyle(src);
    ART_TOKENS.forEach(function (k) {
      var v = cs.getPropertyValue(k);
      if (v && v.trim()) host.style.setProperty(k, v.trim());
    });
  }

  function teardown(triggers) {
    running = false;
    window.clearTimeout(endTimer); window.clearInterval(waveTimer);
    timers.forEach(window.clearTimeout); timers = [];
    if (raf) cancelAnimationFrame(raf);
    raf = endTimer = waveTimer = null;
    if (layer) { layer.remove(); layer = null; }
    items = [];
    document.documentElement.classList.remove('ph-chaos-on');
    triggers.forEach(function (b) { b.classList.remove('is-going'); });
    /* Sweep any ghost, do not rely on its own removal timer. teardown() clears `timers`, which is
       where slideAway parks that timer, so a teardown inside the first 740ms (a double-click on a
       button that dares you to click it, or a tab-hide) cancelled the removal and left a fixed,
       off-screen duplicate advert welded to the page. It reappeared on resize or rotate. */
    [].forEach.call(document.querySelectorAll('.ph-chaos-ghost'), function (g) { g.remove(); });
    restore();
  }

  /* Put the advert back exactly as it was. Called from the happy path AND from teardown, so an
     interrupted run (tab hidden, second click) can never strand the page with a missing advert. */
  function restore() {
    if (!hidden) return;
    var el = hidden; hidden = null;
    el.style.visibility = '';
    el.classList.add('ph-chaos-returning');
    window.setTimeout(function () { el.classList.remove('ph-chaos-returning'); }, RETURN_MS + 60);
  }

  /* Phase 1: the advert detaches and leaves. The REAL element is only made invisible (it keeps its
     box, so nothing below it jumps); what actually flies is a fixed clone, removed when it lands. */
  function slideAway(ad) {
    var box = ad.getBoundingClientRect();
    var ghost = ad.cloneNode(true);
    ghost.className = 'ph-adwin ph-w98win ph-chaos-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    // inert, same as the storm clones: aria-hidden with focusable children is a real a11y fault
    [].forEach.call(ghost.querySelectorAll('a,button,input,select,textarea'), function (n) {
      n.setAttribute('tabindex', '-1');
      n.removeAttribute('href');
      if ('disabled' in n) n.disabled = true;
    });
    ghost.style.left = box.left + 'px';
    ghost.style.top = box.top + 'px';
    ghost.style.width = box.width + 'px';
    ghost.style.setProperty('--slide', (window.innerWidth - box.left + 80) + 'px');
    ghost.style.setProperty('--slide-ms', SLIDE_MS + 'ms');
    /* IN PLACE, as the advert's own next sibling, NOT reparented to <html>.
       Copying tokens onto a reparented clone got the shadow and the fonts right and still left it
       10px short, because "which properties does this inherit" is a question with a long tail:
       line-height came from .ph-art__body, and guessing the rest overshot to 27px too tall.
       Cloning in place makes the question go away - the ghost has the same parent as the thing it
       is standing in for, so it renders identically by construction.
       Safe here where it was not for the storm: the ghost only exists during the SLIDE, which
       finishes ~900ms before .ph-art starts being transformed. */
    ad.parentNode.insertBefore(ghost, ad.nextSibling);
    ad.style.visibility = 'hidden';
    hidden = ad;
    timers.push(window.setTimeout(function () { ghost.remove(); }, SLIDE_MS + 40));
  }

  function spawn(sources) {
    for (var n = 0; n < PER_WAVE && items.length < MAX_LIVE; n++) {
      var src = sources[n % sources.length];
      {
        var el = src.cloneNode(true);
        el.className = 'ph-adwin ph-w98win ph-chaos-item';
        el.removeAttribute('id');
        el.setAttribute('aria-hidden', 'true');
        [].forEach.call(el.querySelectorAll('a,button,input,select,textarea'), function (n) {
          n.setAttribute('tabindex', '-1');
          n.removeAttribute('href');
          if ('disabled' in n) n.disabled = true;
        });
        var it = {
          el: el,
          x: rand(0, Math.max(1, window.innerWidth - 300)),
          y: rand(0, Math.max(1, window.innerHeight - 160)),
          vx: rand(-26, 26) || 12,
          vy: rand(-24, 24) || 10,
          r: rand(0, 360),
          vr: rand(-26, 26),
          s: rand(0.45, 1.35),
          vs: rand(-0.012, 0.012)
        };
        items.push(it);
        layer.appendChild(el);
      }
    }
  }

  function tick() {
    var w = window.innerWidth, h = window.innerHeight;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      it.x += it.vx; it.y += it.vy; it.r += it.vr;
      it.s += it.vs;
      if (it.s < 0.35 || it.s > 1.5) it.vs *= -1;
      // bounce, with a kick so nothing settles into a boring straight line
      if (it.x < -60)      { it.x = -60;     it.vx = Math.abs(it.vx) * rand(0.9, 1.3); it.vr += rand(-12, 12); }
      if (it.x > w - 40)   { it.x = w - 40;  it.vx = -Math.abs(it.vx) * rand(0.9, 1.3); it.vr += rand(-12, 12); }
      if (it.y < -60)      { it.y = -60;     it.vy = Math.abs(it.vy) * rand(0.9, 1.3); it.vr += rand(-12, 12); }
      if (it.y > h - 40)   { it.y = h - 40;  it.vy = -Math.abs(it.vy) * rand(0.9, 1.3); it.vr += rand(-12, 12); }
      it.el.style.transform = 'translate3d(' + it.x + 'px,' + it.y + 'px,0) rotate(' + it.r + 'deg) scale(' + it.s + ')';
    }
    if (running) raf = requestAnimationFrame(tick);
  }

  /* Every advert SHAPE available on this page, not just the ones that got placed. The units live in
     <template data-ph-adtpl> and JS injects them at intervals through the article, so a short post
     may only have one on screen. For a storm we want all of them regardless. */
  function adShapes() {
    var live = [].slice.call(document.querySelectorAll('.ph-adwin:not(.ph-chaos-item):not(.ph-chaos-ghost)'));
    var seen = {}, out = [];
    live.forEach(function (el) {
      var k = (el.className.match(/ph-adwin--(\w+)/) || [])[1] || 'x';
      seen[k] = 1; out.push(el);
    });
    [].forEach.call(document.querySelectorAll('template[data-ph-adtpl]'), function (t) {
      var k = t.getAttribute('data-kind') || 'x';
      if (seen[k]) return;                       // already have this shape from the live page
      var el = t.content && t.content.firstElementChild;
      if (el) { seen[k] = 1; out.push(el); }
    });
    return out;
  }

  function go(trigger) {
    var sources = adShapes();
    if (!sources.length) return;
    var triggers = [].slice.call(document.querySelectorAll('[data-ph-chaos]'));
    triggers.forEach(function (b) { b.classList.add('is-going'); });

    if (calm) { endTimer = window.setTimeout(function () { teardown(triggers); }, 900); return; }
    if (running) { teardown(triggers); triggers.forEach(function (b) { b.classList.add('is-going'); }); }

    running = true;
    // 1. the advert leaves, 2. a beat of nothing, 3. then the storm starts
    var mine = trigger && trigger.closest ? trigger.closest('.ph-adwin') : null;
    if (mine) slideAway(mine);
    timers.push(window.setTimeout(function () {
      if (running) storm(sources, triggers);
    }, (mine ? SLIDE_MS : 0) + BEAT_MS));
  }

  function storm(sources, triggers) {
    layer = document.createElement('div');
    layer.className = 'ph-chaos-layer';
    layer.setAttribute('aria-hidden', 'true');
    carryTokens(layer);
    /* documentElement, not body. A transformed or filtered ancestor becomes the containing block
       for position:fixed children, and this effect deliberately transforms page content while it
       runs. Hanging the layer off <html> puts it above anything we touch, so the flying boxes can
       never be silently re-based against the document height again. */
    document.documentElement.appendChild(layer);
    document.documentElement.classList.add('ph-chaos-on');

    spawn(sources);
    waveTimer = window.setInterval(function () {
      if (!running) return;
      spawn(sources);
      // and kick everything already flying, so it never calms down
      items.forEach(function (it) { it.vx += rand(-8, 8); it.vy += rand(-8, 8); it.vr += rand(-18, 18); });
    }, WAVE_MS);

    raf = requestAnimationFrame(tick);
    endTimer = window.setTimeout(function () { teardown(triggers); }, DURATION);
  }


  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-ph-chaos]') : null;
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();          // the card behind is one big link; do not follow it
    go(t);
  });

  // a storm left running while the tab is hidden is just wasted battery
  document.addEventListener('visibilitychange', function () {
    if (document.hidden && running) teardown([].slice.call(document.querySelectorAll('[data-ph-chaos]')));
  });
})();
